
// Professional error monitoring with Sentry
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

class ErrorMonitoring {
  private isInitialized: boolean = false;
  
  // Initialize error monitoring with Sentry
  init() {
    if (this.isInitialized) return;
    
    // Only initialize Sentry in production
    if (import.meta.env.PROD) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN || "", // Use environment variable instead of hardcoded value
        integrations: [new BrowserTracing()],
        tracesSampleRate: 0.5, // Adjust sampling rate as needed
        environment: "production",
      });
      
      console.log('Sentry error monitoring initialized');
    } else {
      // For development, keep using local error monitoring
      this.setupLocalMonitoring();
      console.log('Local error monitoring initialized (development mode)');
    }
    
    this.isInitialized = true;
  }
  
  // Set up local monitoring for development
  private setupLocalMonitoring() {
    // Set up global error handler
    window.addEventListener('error', (event) => {
      this.captureError({
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        timestamp: Date.now()
      });
      
      // Don't prevent default behavior
      return false;
    });
    
    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        error: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        timestamp: Date.now()
      });
    });
  }
  
  // Capture an error
  captureError(errorEvent: any) {
    if (!this.isInitialized) this.init();
    
    if (import.meta.env.PROD) {
      // In production, use Sentry
      if (errorEvent.error) {
        Sentry.captureException(errorEvent.error);
      } else {
        Sentry.captureMessage(errorEvent.message);
      }
    } else {
      // In development, use local storage
      this.saveLocalError(errorEvent);
    }
    
    // Log error to console for debugging
    console.error('Captured error:', errorEvent);
  }
  
  // Manually report an error
  reportError(error: Error, componentStack?: string) {
    this.captureError({
      message: error.message,
      error,
      componentStack,
      timestamp: Date.now()
    });
  }
  
  // Save errors to localStorage (development only)
  private saveLocalError(errorEvent: any) {
    try {
      // Get existing errors
      const savedErrors = localStorage.getItem('error_logs');
      let errors = savedErrors ? JSON.parse(savedErrors) : [];
      
      // Add new error
      errors.push({
        ...errorEvent,
        error: errorEvent.error ? {
          name: errorEvent.error.name,
          message: errorEvent.error.message,
          stack: errorEvent.error.stack
        } : undefined
      });
      
      // Only keep last 50 errors
      if (errors.length > 50) {
        errors = errors.slice(-50);
      }
      
      localStorage.setItem('error_logs', JSON.stringify(errors));
    } catch (error) {
      console.error('Failed to save error logs:', error);
    }
  }
  
  // Get all captured errors (development only)
  getErrors() {
    if (import.meta.env.DEV) {
      const savedErrors = localStorage.getItem('error_logs');
      return savedErrors ? JSON.parse(savedErrors) : [];
    }
    return [];
  }
  
  // Clear all errors (development only)
  clearErrors() {
    if (import.meta.env.DEV) {
      localStorage.removeItem('error_logs');
    }
  }
}

// Create singleton instance
const errorMonitoring = new ErrorMonitoring();

export default errorMonitoring;
